import React, { useRef, useEffect, MutableRefObject, useState } from "react";
import { useHistory } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useInfiniteQuery } from "@tanstack/react-query";
import { IonSpinner, IonItem, IonLabel, IonList } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { FormattedMessage, useIntl } from "react-intl";
import classNames from "classnames";

// Components
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import SkeletonLoader from "./SkeletonLoader";
import ContactPlatform from "./common/contact-platform";

// Other
import ContactsApi from "../api/contacts";
import useContactsStore from "../store/contacts";
import { Contact, FilterOption, TranslatedString } from "../interfaces";
import serialize, { deserialize } from "../utils/serialize";

// Icons and styles
import {
  IconHourglassLow,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconCalendar,
  IconCreditCard,
  IconArchive,
  IconChevronLeft,
  IconUserFilled,
  IconShoppingBag,
  IconMoneybag,
} from "@tabler/icons-react";
import ConversationsSvg from "../theme/images/conversations.svg";
import "./ChatList.css";
import useCountersStore from "../store/counts";

const MotionLink = motion(IonItem);

interface ILayoutProps {
  select?: (id: string) => void;
  mode: string;
  isMulti: boolean;
  loading?: boolean;
  styles?: { [key: string]: string };
  type?: string; // "chats" | "leads"
  selectedId?: string;
}

const ChatList: React.FC<ILayoutProps> = ({
  select,
  mode,
  isMulti,
  loading = false,
  type = "chats",
  styles = {},
  selectedId = "",
}) => {
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const parentRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const filters = useContactsStore((state) => state.filters);
  const updateContactsDb = useContactsStore((state) => state.updateContactsDb);
  const contactList = useContactsStore((state) => state.contactList);
  const isSearch = useContactsStore((state) => state.searchActive);

  const archivedContacts = useCountersStore((state) => state.archived);

  const setAvailableActions = useContactsStore(
    (state) => state.setAvailableActions,
  );
  const availableActions = useContactsStore((state) => state.availableActions);

  const setFilters = useContactsStore((state) => state.setFilters);
  const history = useHistory();
  const hasInitializedFilters = useRef(false);

  const isPendingBucket = filters.find(
    (f) => f.label === "bucket" && f.value === "pending",
  )?.isActive;

  const displayArchived = archivedContacts > 0 && type === "chats" && !isSearch;
  const isArchivedBucket = filters.find(
    (f) => f.label === "bucket" && f.value === "archived",
  )?.isActive;

  const areFiltersActive = filters.some(
    (f) => f.isActive && ["search", "bucket"].indexOf(f.label) === -1,
  );

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    error,
  } = useInfiniteQuery({
    queryKey: ["getContacts", filters],
    queryFn: ({ pageParam }) =>
      ContactsApi.getContacts({ filter: filters, pageParam }),
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? null,
    refetchOnWindowFocus: Capacitor.isNativePlatform() ? true : false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: filtersInitialized,
  });

  console.log(type);

  const results = contactList;
  const prevResultsLength = useRef(results.length);
  const rowVirtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (type === "chats" ? 68 : type === "kanban" ? 120 : 40), // review size on mobile
    overscan: mode === "desktop" ? 8 : 4,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (!hasInitializedFilters.current) return;
    const objecjParams = filters.reduce<Record<string, any>>((acc, filter) => {
      if (filter.isActive) {
        acc[filter.label] = filter.value;
      }
      return acc;
    }, {});
    const searchParams = serialize(objecjParams);
    const url = `${history.location.pathname}${
      searchParams ? `?${searchParams}` : ""
    }`;
    history.replace(url);
  }, [filters]);

  useEffect(() => {
    if (hasInitializedFilters.current) return;
    const params = history.location.search;
    const paramsObject = deserialize(params);
    const updatedFilters = filters.map((filter) => {
      if (
        paramsObject.hasOwnProperty(filter.label) &&
        paramsObject[filter.label]
      ) {
        return {
          ...filter,
          isActive: true,
          value:
            filter.type === "list"
              ? decodeURIComponent(paramsObject[filter.label]).split(",")
              : filter.type === "toggle"
              ? paramsObject[filter.label] === "true"
              : paramsObject[filter.label],
        };
      } else {
        return {
          ...filter,
          value: filter.type === "list" ? [] : filter.value,
          isActive: false,
        };
      }
    });
    hasInitializedFilters.current = true;
    setFilters(updatedFilters);
    setFiltersInitialized(true);
  }, []);

  useEffect(() => {
    const prev = prevResultsLength.current;
    const current = results.length;
    if (prev === 0 && current > 0) {
      rowVirtualizer.scrollToIndex(0);
    }
    prevResultsLength.current = current;
  }, [results.length]);

  useEffect(() => {
    if (!infiniteData || !infiniteData.pages.length) return;
    const lastPage = infiniteData?.pages.at(-1);
    const allContacts = infiniteData.pages.flatMap(
      (page) => page?.contacts || [],
    );

    setAvailableActions(lastPage?.actionsList || []);
    updateContactsDb(allContacts || [], {
      isSearch: lastPage?.isSearch || false, // Treat search results differently
    });
  }, [infiniteData?.pages]);

  useEffect(() => {
    if (isSearch || isFetching || !hasNextPage) {
      return;
    }
    const lastIndex = virtualItems.at(-1)?.index;
    const atBottom = lastIndex === results.length - 1;
    // debug variables
    //console.log(`🧪 [Virtualizer] Items: ${virtualItems.length}, Results: ${results.length}, hasNextPage: ${hasNextPage}`);
    if (atBottom) {
      console.log("Fetching next page...");
      fetchNextPage();
    }
  }, [virtualItems.length, results?.length, isSearch]);

  const handleArchived = () => {
    const bucket = filters.find((f) => f.label === "bucket");

    let updated: FilterOption[];

    if (bucket) {
      if (bucket.value === "archived") {
        updated = filters.map((f) =>
          f.label === "bucket"
            ? {
                label: "bucket",
                type: "bucket",
                isActive: false,
                value: "",
                isVisible: false,
              }
            : f,
        );
      } else {
        updated = filters.map((f) =>
          f.label === "bucket"
            ? { ...f, value: "archived", isActive: true }
            : f,
        );
      }
    } else {
      updated = [
        ...filters,
        { label: "bucket", type: "bucket", value: "archived", isActive: true },
      ];
    }

    setFilters(updated);
  };

  const LeadItem = React.memo<{
    contact: Contact;
    isMulti: boolean;
    styles: { [key: string]: string };
    availableActions?: string[];
  }>(({ contact, isMulti, styles, availableActions }) => {
    const intl = useIntl();
    const currentLanguage = intl.locale as keyof TranslatedString;
    const {
      timestampHour,
      name,
      phoneNumber,
      escalated,
      manual,
      blocked,
      companies,
      score,
    } = contact;
    const intent = contact.intent?.[currentLanguage];
    const intentDescription = contact.intentDescription?.[currentLanguage];
    return (
      <>
        <IonLabel>
          <div
            className={`d-flex justify-content-between align-items-center fs-2 color-step-800 ${styles.listItemHolder}`}
          >
            <div className="d-flex align-items-center gap-2">
              <div className={`${styles.date} `}>{timestampHour}</div>

              {isMulti ? (
                <div className={`${styles.account}`}>
                  <span>{companies.map((c: any) => c.name)}</span>
                </div>
              ) : null}
              <div className={styles.platform}>
                <ContactPlatform
                  contact={contact}
                  defaultClass={styles.platform}
                />
              </div>
              <div className={`${styles.name}`}>
                <span>{name || phoneNumber}</span>
              </div>

              <div className={styles.score}>
                <span
                  className={classNames("badge", {
                    "text-bg-danger": score === "low",
                    "text-bg-warning": score === "medium",
                    "text-bg-success": score === "high",
                    "text-bg-light": "no_score" === score || !score,
                    [styles.noScore]: score === "no_score" || !score,
                  })}
                >
                  <FormattedMessage id={`leads.${score}`} />
                </span>
              </div>
              <div className={styles.action}>
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <Tooltip className="visito-tooltip">
                      {intentDescription ||
                        intl.formatMessage({
                          id: "leads.noIntentExplanation",
                        })}
                    </Tooltip>
                  }
                >
                  <span
                    className={classNames("badge text-bg-light", {
                      [styles.noScore]: !intent,
                    })}
                  >
                    {intent || intl.formatMessage({ id: "leads.noIntent" })}
                  </span>
                </OverlayTrigger>
              </div>
              <div className={styles.action}>
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <Tooltip className="visito-tooltip">
                      <FormattedMessage
                        id={`leads.followUpStatus.${contact.followUpStatus}Explanation`}
                        values={{ count: contact.successfulFollowUps }}
                      />
                    </Tooltip>
                  }
                >
                  <div
                    className={classNames("badge text-bg-light", {
                      [styles.noScore]:
                        contact.followUpStatus === "no_follow_up",
                      "text-bg-warning": contact.followUpStatus === "pending",
                      "text-bg-success":
                        contact.followUpStatus === "followed_up",
                      "text-bg-danger": contact.followUpStatus === "rejected",
                    })}
                  >
                    <div className="d-flex align-items-center gap-2">
                      {contact.followUpStatus === "pending" && (
                        <IconHourglassLow size={13} />
                      )}
                      {contact.followUpStatus === "followed_up" && (
                        <IconCircleCheckFilled size={13} />
                      )}
                      {contact.followUpStatus === "rejected" && (
                        <IconCircleXFilled size={13} />
                      )}
                      <FormattedMessage
                        id={`leads.followUpStatus.${contact.followUpStatus}`}
                      />
                      {contact.successfulFollowUps ? (
                        <>
                          {" - "}
                          <FormattedMessage
                            id={`leads.followUpStatus.howManySent`}
                            values={{ count: contact.successfulFollowUps }}
                          />
                        </>
                      ) : null}
                    </div>
                  </div>
                </OverlayTrigger>
              </div>
              <div
                className={`${styles.actions} d-flex align-items-center gap-1`}
              >
                {contact.actionsList
                  ?.filter((a) => availableActions?.includes(a))
                  ?.map((action) => {
                    let icon;
                    switch (action) {
                      case "availability":
                        icon = <IconCalendar size={14} className="mr-1" />;
                        break;
                      case "payment_received":
                        icon = <IconCreditCard size={14} className="mr-1" />;
                        break;
                      default:
                        icon = <IconCircleXFilled size={14} className="mr-1" />;
                        break;
                    }

                    return (
                      <OverlayTrigger
                        key={action}
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={
                          <Tooltip className="visito-tooltip">
                            <FormattedMessage
                              id={`leads.stats.${action}Explanation`}
                            />
                          </Tooltip>
                        }
                      >
                        <div className="badge text-bg-light">
                          <div
                            key={action}
                            className="d-flex align-items-center"
                          >
                            {icon}
                            <FormattedMessage id={`leads.stats.${action}`} />
                          </div>
                        </div>
                      </OverlayTrigger>
                    );
                  })}
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 justify-content-end">
              {escalated ? (
                <div className="badge text-bg-warning">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div className="fw-semibold-1">
                      <FormattedMessage id="esc.needsReview" />
                    </div>
                  </div>
                </div>
              ) : null}
              {manual ? (
                <div className="badge text-bg-info">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div className="fw-semibold-1">
                      <FormattedMessage id="chats.enabled" />
                    </div>
                  </div>
                </div>
              ) : null}
              {blocked ? (
                <div className="badge text-bg-danger">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div className="fw-semibold-1">
                      <FormattedMessage id="chats.blocked" />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </IonLabel>
      </>
    );
  });

  const Item = React.memo<{ contact: Contact; isMulti: boolean }>(
    ({ contact, isMulti }) => {
      const {
        name,
        preview,
        timestamp,
        unread,
        phoneNumber,
        escalated,
        manual,
        blocked,
        companies,
      } = contact;
      return (
        <>
          <div slot="start">
            <div className="profile">
              <ContactPlatform
                defaultClass="profile-platform"
                contact={contact}
              />
            </div>
            {type === "kanban" && <div className="kanban"></div>}
          </div>
          <IonLabel className="ion-text-nowrap">
            <div className="contact">
              <p className="contact-holder">
                {isMulti &&
                  companies?.map((c) => (
                    <span className="contact-company" key={c.id}>
                      <span className="badge text-bg-light">{c.name}</span>
                    </span>
                  ))}
                <span className="contact-name fw-semibold-1">
                  {name || phoneNumber}
                </span>
              </p>
              {type !== "kanban" ? (
                <p className="conversation-preview">{preview}</p>
              ) : (
                <div className="lead-value d-flex align-items-center gap-1 color-step-750">
                  <IconShoppingBag size={16} className="me-1" />
                  <div className=" fs-2">23/02 to 25/02 | 3 guests</div>
                </div>
              )}
            </div>
            {type === "kanban" && (
              <div className="kanban">
                <div className="lead-value d-flex align-items-center gap-1 color-step-750">
                  <IconMoneybag size={16} />
                  <div className="color-step-750 fs-2">240 USD</div>
                </div>
                <div className="lead-value d-flex align-items-center gap-1 color-step-750">
                  <IconCreditCard size={16} />
                  <div className="color-step-750 fs-2">Failed</div>
                </div>
              </div>
            )}
          </IonLabel>
          <div slot="end">
            <div className="details">
              <div className="details-container">
                <div className="half-item timestamp">
                  {timestamp}{" "}
                  {unread ? (
                    <div className="notifications-badge badge text-bg-info">
                      {unread}
                    </div>
                  ) : null}
                  {contact.assignations.map((a, key) => (
                    <OverlayTrigger
                      key={key}
                      delay={{ show: 150, hide: 400 }}
                      placement="bottom"
                      overlay={
                        <Tooltip className="visito-tooltip">{a.email}</Tooltip>
                      }
                    >
                      <div
                        className="notifications-badge badge text-bg-light"
                        key={a.id}
                      >
                        <div className="flex align-items-center gap-1">
                          <IconUserFilled size={12} className="text-muted" />{" "}
                          <span className="fw-1 color-step-750">
                            {a.email.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </OverlayTrigger>
                  ))}
                </div>
                <div className="half-item">
                  {escalated ? (
                    <div className="badge text-bg-warning">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div className="fw-semibold-1">
                          <FormattedMessage id="esc.needsReview" />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {manual ? (
                    <div className="badge text-bg-info ml-1">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div className="fw-semibold-1">
                          <FormattedMessage id="chats.enabled" />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {blocked ? (
                    <div className="badge text-bg-danger">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div className="fw-semibold-1">
                          <FormattedMessage id="chats.blocked" />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            {type === "kanban" && <div className="kanban"></div>}
          </div>
        </>
      );
    },
  );

  const KanbanItem = Item;

  if (loading || (isFetching && results.length === 0)) {
    return <SkeletonLoader rows={10} />;
  }

  if (error) {
    return (
      <div className="p-3 color-step-500">
        <FormattedMessage
          id="chats.errorLoading"
          defaultMessage="Error loading chats"
        />
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div className="empty-list">
        <div className="welcome-content text-center">
          <div className="row justify-content-center">
            <div className="mb-3">
              <img src={ConversationsSvg} alt="" />
            </div>
            <span className="color-step-900 font-14 fw-semibold-1">
              {isSearch || areFiltersActive ? (
                <FormattedMessage id="chats.emptyStateEscTitle" />
              ) : isPendingBucket ? (
                <FormattedMessage id="chats.emptyStatePendingTitle" />
              ) : (
                <FormattedMessage id="chats.emptyStateTitle" />
              )}
            </span>
            <span className="color-step-600 font-12">
              {isSearch || areFiltersActive ? (
                <FormattedMessage id="chats.emptyStateEscSubtitle" />
              ) : isPendingBucket ? (
                <FormattedMessage id="chats.emptyStatePendingSubtitle" />
              ) : (
                <FormattedMessage id="chats.emptyStateSubtitle" />
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const virtualizerHeight = displayArchived
    ? `${rowVirtualizer.getTotalSize() + 49}px`
    : `${rowVirtualizer.getTotalSize()}px`;

  return (
    <div className="chat-list-items-holder" ref={parentRef}>
      <div style={{ height: virtualizerHeight }}>
        <IonList
          style={{
            padding: 0,
            margin: 0,
            width: "100%",
            position: "relative",
            height: virtualizerHeight,
          }}
          className="contact-list"
        >
          {displayArchived && (
            <IonItem
              lines="full"
              className="list-item pointer"
              detail={false}
              onClick={handleArchived}
            >
              <div slot="start">
                {isArchivedBucket ? (
                  <IconChevronLeft className="color-step-600" size={18} />
                ) : (
                  <IconArchive className="color-step-600" size={18} />
                )}
              </div>
              <IonLabel className="ion-text-nowrap">
                <span className="color-step-900 fs-3">
                  <FormattedMessage id="chats.archived" />
                </span>
              </IonLabel>
              <div slot="end" className="">
                <span className="mb-0 fs-2 color-step-600">
                  {archivedContacts}
                </span>
              </div>
            </IonItem>
          )}
          <AnimatePresence>
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const { index, start, size } = virtualItem;
              const contact = results[index];
              const { id } = contact;
              const onClick = select ? () => select(id) : undefined;
              return (
                <MotionLink
                  lines="full"
                  className={classNames("list-item", {
                    selected: id === selectedId && type !== "leads",
                    [styles.listItem]: type === "leads",
                  })}
                  detail={false}
                  onClick={onClick}
                  key={`${id}`}
                  style={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    height: `${size}px`,
                    transform: `translateY(${start}px)`,
                  }}
                  initial={false}
                  animate={{ y: start }}
                  exit={{ opacity: 0 }}
                  transition={{ ease: "linear", duration: 0.2 }}
                >
                  {type === "leads" ? (
                    <LeadItem
                      contact={contact}
                      availableActions={availableActions}
                      isMulti={isMulti}
                      styles={styles}
                    />
                  ) : type === "chats" ? (
                    <Item contact={contact} isMulti={isMulti} />
                  ) : (
                    <KanbanItem contact={contact} isMulti={isMulti} />
                  )}
                </MotionLink>
              );
            })}
          </AnimatePresence>
        </IonList>
        {isFetching && (
          <div className="text-center py-2">
            <IonSpinner name="dots" style={{ width: "38px", height: "38px" }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ChatList);
